import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AudioService } from '@core/audio/audio.service';
import { Text } from '@features/lp/lp-text/lp-text.utils';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { waitForMillis } from '@shared/delay.utils';

@Component({
  selector: 'lp-text',
  imports: [],
  templateUrl: './lp-text.component.html',
  styleUrl: './lp-text.component.css',
})
export class LpTextComponent {
  readonly #audioService = inject(AudioService);
  readonly #lpVariablesService = inject(LpVariablesService);

  // inputs
  text = input.required<string>();
  animate = input<boolean>(false);
  noSfx = input<boolean>(false);

  // typing
  textObject = computed<Text>(() =>
    Text.fromString(this.text(), {
      variables: this.#lpVariablesService.namedDictionary(),
    }),
  );
  shownText = signal<Text>(new Text());
  started = output();
  finished = output();

  constructor() {
    toObservable(this.textObject).subscribe(async (textObject) => {
      await this.type(textObject);
    });
  }

  private async type(text: Text) {
    this.started.emit();

    for (let i = 1; i < text.length; i++) {
      if (!this.animate()) break;

      if (text.at(i) === '.' && text.at(i - 1) === '.') {
        i++;
        continue;
      }

      const subText = text.slice(0, i);
      this.shownText.set(subText);
      const character = subText.at(-1)!;
      const isLetter = character.match(/[a-z]/i);
      if (isLetter && !this.noSfx()) {
        this.#audioService.play('/assets/audio/type.wav', { volume: 0.25 });
      }
      await waitForMillis(this.getCharacterDelay(character));
    }

    this.shownText.set(text);
    this.finished.emit();
  }

  private getCharacterDelay(char: string) {
    switch (char) {
      case ',':
        return 200;
      case '!':
      case '?':
      case '.':
      case ':':
      case ';':
        return 400;
      case '\n':
        return 500;
      default:
        return 30;
    }
  }
}
